<template>
  <BaseModal
    v-model="isVisible"
    :size="isMobile ? 'full' : '75vw'"
    :closable="true"
    :close-on-overlay="!isMobile"
    :persistent="false"
    @close="handleClose"
    class="ticket-detail-modal"
    :class="{ 'mobile-modal': isMobile }"
  >
    <!-- Header compacto con información esencial -->
    <div class="bg-white border-b border-gray-200 -m-6 mb-6" :class="isMobile ? 'px-4 py-3' : 'px-6 py-4'">
      <div class="flex items-center justify-between" :class="{ 'flex-col gap-3': isMobile }">
        <!-- Info básica del ticket -->
        <div class="flex items-center gap-3" :class="{ 'w-full': isMobile }">
          <!-- Avatar compacto solo en móvil -->
          <div v-if="isMobile" class="flex-shrink-0">
            <div v-if="ticket?.assignedTo === 'Elias AI'" class="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
              <svg class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <div class="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white"></div>
            </div>
            <div v-else-if="ticket?.assignedToAvatar" class="w-10 h-10 rounded-xl overflow-hidden shadow-lg">
              <img :src="ticket.assignedToAvatar" :alt="ticket.assignedTo" class="w-full h-full object-cover" @error="handleAvatarError" />
              <div class="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white"></div>
            </div>
            <div v-else class="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center text-white font-semibold text-sm shadow-lg">
              {{ getAssigneeInitials(ticket?.assignedTo) }}
              <div class="absolute -bottom-1 -right-1 w-4 h-4 bg-slate-400 rounded-full border-2 border-white"></div>
            </div>
          </div>
          
          <!-- Solo icono de ticket para desktop, título y ID -->
          <div class="flex items-center gap-3 flex-1 min-w-0">
            <!-- Icono de ticket para desktop -->
            <div v-if="!isMobile" class="flex-shrink-0">
              <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                <svg class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a1.994 1.994 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
            </div>
            
            <!-- Título y ID -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-1">
                <span class="font-mono text-sm text-blue-600 font-semibold">#{{ ticket?.id }}</span>
                <div :class="getStatusIndicatorClass(ticket?.status)" class="w-2 h-2 rounded-full animate-pulse"></div>
                <span class="text-sm font-medium text-gray-600">{{ formatStatusText(ticket?.status) }}</span>
              </div>
              <h1 class="font-bold text-gray-900 truncate" :class="isMobile ? 'text-lg' : 'text-xl'">
                {{ ticket?.title }}
              </h1>
            </div>
          </div>
        </div>
        
        <!-- Información rápida de estado solo en móvil -->
        <div v-if="isMobile" class="flex items-center gap-4 w-full justify-between">
          <div class="flex items-center gap-2 text-sm">
            <span class="text-gray-500">Prioridad:</span>
            <span v-if="ticket?.priority === 'alta' || ticket?.priority === 'high'" class="text-red-600 font-semibold">🔥 Alta</span>
            <span v-else-if="ticket?.priority === 'media' || ticket?.priority === 'medium'" class="text-yellow-600 font-semibold">⚡ Media</span>
            <span v-else class="text-green-600 font-semibold">🟢 Baja</span>
          </div>
          <div class="flex items-center gap-2 text-sm">
            <span class="text-gray-500">Progreso:</span>
            <div class="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div :class="getProgressBarClass(ticket?.status)" class="h-full transition-all duration-300" :style="`width: ${getProgressPercentage(ticket)}%`"></div>
            </div>
            <span class="font-semibold text-gray-700">{{ getProgressPercentage(ticket) }}%</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Contenido principal con tabs prominentes -->
    <div class="flex flex-col h-full" :style="isMobile ? 'min-height: 70vh;' : 'min-height: 650px;'">
      <!-- Navigation tabs modernos y limpios -->
      <div class="mb-6">
        <!-- Container con diseño minimalista -->
        <div class="bg-white border-b border-gray-200">
          <nav class="-mb-px flex space-x-8" :class="{ 'space-x-4': isMobile }">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              @click="activeTab = tab.id"
              class="relative flex items-center whitespace-nowrap border-b-2 font-medium text-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-t-lg"
              :class="[
                isMobile ? 'py-3 px-2' : 'py-4 px-1',
                {
                  'border-blue-500 text-blue-600': activeTab === tab.id,
                  'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300': activeTab !== tab.id
                }
              ]"
            >
              <!-- Icono del tab -->
              <component 
                :is="tab.icon" 
                class="flex-shrink-0 transition-colors duration-200" 
                :class="[
                  isMobile ? 'w-4 h-4' : 'w-5 h-5',
                  activeTab === tab.id ? 'text-blue-600' : 'text-gray-400'
                ]" 
              />
              
              <!-- Label del tab -->
              <span 
                class="ml-2 font-medium truncate" 
                v-if="!isMobile || !tab.badge"
                :class="{
                  'text-blue-600': activeTab === tab.id,
                  'text-gray-500': activeTab !== tab.id
                }"
              >
                {{ isMobile ? (tab.shortLabel || tab.label) : tab.label }}
              </span>
              
              <!-- Badge de notificaciones -->
              <span 
                v-if="tab.badge" 
                class="ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium transition-colors duration-200"
                :class="[
                  {
                    'bg-blue-100 text-blue-800': activeTab === tab.id,
                    'bg-red-100 text-red-800': activeTab !== tab.id && tab.id === 'communication',
                    'bg-gray-100 text-gray-800': activeTab !== tab.id && tab.id !== 'communication'
                  }
                ]"
              >
                {{ tab.badge }}
              </span>
              
              <!-- Indicador de tab activo -->
              <div 
                v-if="activeTab === tab.id" 
                class="absolute inset-x-0 bottom-0 h-0.5 bg-blue-500 rounded-t-sm"
              ></div>
            </button>
          </nav>
        </div>
      </div>
      <!-- Tab content - Mobile optimized -->
      <div class="flex-1 overflow-hidden">
        <!-- Tab: Detalles -->
        <div v-if="activeTab === 'details'" class="h-full overflow-y-auto">
          <div class="grid grid-cols-1 gap-4" :class="{ 'lg:grid-cols-4 gap-6': !isMobile }">
            <!-- Información principal optimizada -->
            <div class="space-y-4" :class="{ 'lg:col-span-3 space-y-6': !isMobile }">
              <!-- Descripción -->
              <div class="bg-white rounded-xl border border-gray-200" :class="isMobile ? 'p-4' : 'p-6'">
                <h3 class="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <svg class="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                  Descripción del Problema
                  <div v-if="isMarkdownContent(ticket?.description)" class="ml-auto">
                    <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <svg class="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
                      </svg>
                      Markdown
                    </span>
                  </div>
                </h3>
                <div class="prose prose-sm prose-blue max-w-none">
                  <!-- Contenedor con altura limitada y scroll -->
                  <div class="max-h-60 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400">
                    <div 
                      v-if="isMarkdownContent(ticket?.description)"
                      v-dompurify-html="getMarkdownContent(ticket?.description)"
                      class="markdown-content"
                    ></div>
                    <p 
                      v-else 
                      class="text-gray-700 whitespace-pre-wrap leading-relaxed"
                    >
                      {{ ticket?.description }}
                    </p>
                  </div>
                </div>
              </div>

            </div>

            <!-- Panel lateral super compacto -->
            <div class="space-y-3" v-if="!isMobile">
              <!-- Info esencial del ticket -->
              <div class="bg-white rounded-lg border border-gray-200 p-4">
                <div class="flex items-center gap-2 mb-3">
                  <svg class="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                  <h3 class="text-sm font-semibold text-gray-900">Información del Ticket</h3>
                </div>
                
                <div class="space-y-3">
                  <!-- ID y Estado -->
                  <div class="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div class="flex items-center gap-2">
                      <div :class="getStatusIndicatorClass(ticket?.status)" class="w-2 h-2 rounded-full"></div>
                      <span class="text-xs font-medium text-gray-600">Estado</span>
                    </div>
                    <span class="text-xs font-semibold text-gray-900">{{ formatStatusText(ticket?.status) }}</span>
                  </div>
                  
                  <!-- Prioridad -->
                  <div class="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div class="flex items-center gap-2">
                      <div class="w-2 h-2 rounded-full" :class="getPriorityIndicatorClass(ticket?.priority)"></div>
                      <span class="text-xs font-medium text-gray-600">Prioridad</span>
                    </div>
                    <span class="text-xs font-semibold" :class="getPriorityTextClass(ticket?.priority)">
                      {{ formatPriorityText(ticket?.priority) }}
                    </span>
                  </div>
                  
                  <!-- Categoría/Lista de ClickUp -->
                  <div class="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div class="flex items-center gap-2">
                      <svg class="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
                      </svg>
                      <span class="text-xs font-medium text-gray-600">Lista</span>
                    </div>
                    <span class="text-xs font-semibold text-gray-900">{{ getTicketCategory(ticket) }}</span>
                  </div>
                  
                  <!-- Solicitante -->
                  <div class="flex items-center justify-between p-2 bg-gray-50 rounded-lg" v-if="ticket?.requesterName">
                    <div class="flex items-center gap-2">
                      <svg class="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                      </svg>
                      <span class="text-xs font-medium text-gray-600">Solicitante</span>
                    </div>
                    <span class="text-xs font-semibold text-gray-900 truncate max-w-20" :title="ticket?.requesterName">
                      {{ ticket?.requesterName }}
                    </span>
                  </div>
                  
                  <!-- Escalado -->
                  <div class="flex items-center justify-between p-2 rounded-lg" :class="ticket?.escalated ? 'bg-red-50' : 'bg-gray-50'" v-if="ticket?.escalated !== undefined">
                    <div class="flex items-center gap-2">
                      <svg class="w-3 h-3" :class="ticket?.escalated ? 'text-red-500' : 'text-gray-400'" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                      </svg>
                      <span class="text-xs font-medium text-gray-600">Escalado</span>
                    </div>
                    <span class="text-xs font-semibold" :class="ticket?.escalated ? 'text-red-700' : 'text-gray-900'">
                      {{ ticket?.escalated ? 'Sí' : 'No' }}
                    </span>
                  </div>
                  
                  <!-- Tiempo estimado -->
                  <div class="flex items-center justify-between p-2 bg-gray-50 rounded-lg" v-if="getTimeEstimate(ticket)">
                    <div class="flex items-center gap-2">
                      <svg class="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      <span class="text-xs font-medium text-gray-600">Tiempo Est.</span>
                    </div>
                    <span class="text-xs font-semibold text-gray-900">{{ getTimeEstimate(ticket) }}</span>
                  </div>
                </div>
              </div>

              <!-- Progreso visual compacto -->
              <div class="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border border-blue-200/50 p-3">
                <div class="text-center mb-3">
                  <div class="text-xs font-semibold text-gray-600">Progreso</div>
                </div>
                <!-- Barra de progreso circular -->
                <div class="flex justify-center mb-3">
                  <div class="relative w-16 h-16">
                    <!-- Círculo de fondo -->
                    <svg class="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
                      <path 
                        d="m18,2.0845 a 15.9155,15.9155 0 0,1 0,31.831 a 15.9155,15.9155 0 0,1 0,-31.831"
                        fill="none" 
                        stroke="#e5e7eb" 
                        stroke-width="2.5" 
                        stroke-dasharray="100, 100"
                      />
                      <!-- Círculo de progreso -->
                      <path 
                        d="m18,2.0845 a 15.9155,15.9155 0 0,1 0,31.831 a 15.9155,15.9155 0 0,1 0,-31.831"
                        fill="none" 
                        :stroke="getProgressColor(ticket?.status)" 
                        stroke-width="2.5" 
                        :stroke-dasharray="`${getProgressPercentage(ticket)}, 100`"
                        stroke-linecap="round"
                        class="transition-all duration-700 ease-out"
                      />
                    </svg>
                    <!-- Porcentaje en el centro -->
                    <div class="absolute inset-0 flex items-center justify-center">
                      <span class="text-sm font-bold text-gray-700">{{ getProgressPercentage(ticket) }}%</span>
                    </div>
                  </div>
                </div>
                <!-- Timeline mini -->
                <div class="space-y-2">
                  <div class="flex items-center gap-2 text-xs">
                    <div class="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span class="text-gray-600">Creado {{ formatDate(ticket?.createdAt) }}</span>
                  </div>
                  <div v-if="ticket?.status !== 'abierto'" class="flex items-center gap-2 text-xs">
                    <div class="w-2 h-2 bg-yellow-500 rounded-full"></div>
                    <span class="text-gray-600">En progreso</span>
                  </div>
                  <div v-if="isTicketResolved(ticket?.status)" class="flex items-center gap-2 text-xs">
                    <div class="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span class="text-gray-600">Resuelto</span>
                  </div>
                </div>
              </div>

              <!-- Enlaces externos mini -->
              <div v-if="ticket?.clickupUrl" class="bg-white rounded-lg border border-gray-200 p-3">
                <a
                  :href="ticket.clickupUrl"
                  target="_blank"
                  class="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
                >
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                  </svg>
                  ClickUp
                </a>
              </div>

              <!-- Tags mini -->
              <div v-if="ticket?.tags && ticket.tags.length > 0" class="bg-white rounded-lg border border-gray-200 p-3">
                <div class="text-xs font-semibold text-gray-900 mb-2">Tags</div>
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="tag in ticket.tags.slice(0, 3)"
                    :key="tag"
                    class="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded font-medium"
                  >
                    {{ tag }}
                  </span>
                  <span v-if="ticket.tags.length > 3" class="text-xs text-gray-500">+{{ ticket.tags.length - 3 }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tab: Comunicación Compacto -->
        <div v-if="activeTab === 'communication'" class="h-full flex flex-col">
          <!-- Header compacto del chat -->
          <div class="flex items-center bg-white border-b border-gray-200 p-2">
            <!-- Avatar mini -->
            <div class="flex-shrink-0 mr-2">
              <div v-if="ticket?.assignedTo === 'Elias AI'" class="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center relative">
                <svg class="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <div class="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-green-400 rounded-full border border-white"></div>
              </div>
              <div v-else class="w-6 h-6 rounded-full bg-slate-600 flex items-center justify-center text-white text-xs font-semibold">
                {{ getAssigneeInitials(ticket?.assignedTo) }}
              </div>
            </div>
            
            <!-- Info compacta -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <h3 class="text-sm font-medium text-gray-900 truncate">{{ ticket?.assignedTo || 'Soporte' }}</h3>
                <div class="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
                <span class="text-xs text-gray-500">En línea</span>
              </div>
            </div>
            
            <!-- Acciones mínimas -->
            <button 
              @click="refreshChat" 
              :disabled="isRefreshing"
              class="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
              title="Actualizar"
            >
              <svg :class="{ 'animate-spin': isRefreshing }" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
            </button>
          </div>
          
          <!-- Área de mensajes compacta -->
          <div ref="messagesContainer" class="flex-1 overflow-y-auto p-3 space-y-2">
            <!-- Mensaje de bienvenida compacto - Solo lectura -->
            <div v-if="messages.length === 0" class="text-center py-6">
              <div class="w-12 h-12 mx-auto mb-3 bg-orange-100 rounded-full flex items-center justify-center">
                <svg class="w-6 h-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
                </svg>
              </div>
              <h3 class="text-base font-semibold text-gray-900 mb-2">Sin comentarios del agente</h3>
              <p class="text-sm text-gray-600 mb-2"><strong>{{ ticket?.assignedTo || 'El agente humano' }}</strong> aún no ha registrado comentarios en este ticket</p>
              <p class="text-xs text-gray-500">Los mensajes aparecerán automáticamente cuando el equipo de soporte agregue comentarios en el sistema</p>
            </div>

            <!-- Lista de mensajes compacta -->
            <div v-for="message in messages" :key="message.id" class="flex items-start gap-2" :class="message.isFromUser ? 'flex-row-reverse' : 'flex-row'">
              <!-- Avatar mini -->
              <div class="flex-shrink-0">
                <div v-if="!message.isFromUser" class="w-6 h-6 rounded-full overflow-hidden bg-slate-600 flex items-center justify-center text-white text-xs">
                  <div v-if="ticket?.assignedTo === 'Elias AI'" class="w-full h-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                    <svg class="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <img v-else-if="ticket?.assignedToAvatar" :src="ticket.assignedToAvatar" :alt="ticket.assignedTo" class="w-full h-full object-cover" @error="handleAvatarError" />
                  <span v-else>{{ getAssigneeInitials(ticket?.assignedTo) }}</span>
                </div>
                <div v-else class="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-semibold">
                  T
                </div>
              </div>
              
              <!-- Contenido del mensaje compacto -->
              <div class="flex-1">
                <div 
                  class="relative px-3 py-2 rounded-lg shadow-sm"
                  :class="{
                    'bg-blue-500 text-white': message.isFromUser,
                    'bg-white text-gray-900 border border-gray-200': !message.isFromUser
                  }"
                >
                  <p class="text-sm whitespace-pre-wrap">{{ message.content }}</p>
                  
                  <!-- Timestamp y estado compactos -->
                  <div class="flex items-center justify-between mt-1 text-xs" :class="message.isFromUser ? 'text-blue-100' : 'text-gray-500'">
                    <span>{{ formatMessageTime(message.timestamp) }}</span>
                    <div v-if="message.isFromUser" class="flex items-center">
                      <svg v-if="message.status === 'sending'" class="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"/>
                      </svg>
                      <svg v-else-if="message.status === 'sent'" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                      </svg>
                      <svg v-else-if="message.status === 'delivered'" class="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      <div v-else-if="message.status === 'read'" class="flex items-center text-blue-200">
                        <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                        <svg class="w-3 h-3 -ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Indicador de escritura compacto -->
            <div v-if="isTyping" class="flex items-start gap-2">
              <div class="w-6 h-6 rounded-full bg-slate-600 flex items-center justify-center text-white text-xs flex-shrink-0">
                <div v-if="ticket?.assignedTo === 'Elias AI'" class="w-full h-full bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                  <svg class="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <span v-else>{{ getAssigneeInitials(ticket?.assignedTo) }}</span>
              </div>
              <div class="bg-white rounded-lg px-3 py-2 shadow-sm border border-gray-200">
                <div class="flex items-center space-x-2">
                  <div class="flex space-x-1">
                    <div class="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce"></div>
                    <div class="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style="animation-delay: 0.1s;"></div>
                    <div class="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style="animation-delay: 0.2s;"></div>
                  </div>
                  <span class="text-xs text-gray-600">escribiendo...</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Mensaje informativo - Chat en modo solo lectura -->
          <div class="bg-blue-50 border-t border-blue-200 p-3">
            <div class="flex items-center gap-2 text-center justify-center">
              <svg class="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
              </svg>
              <span class="text-sm text-blue-700 font-medium">
                Chat en modo solo lectura - Puedes ver los mensajes del equipo de soporte
              </span>
            </div>
          </div>
        </div>

        <!-- Tab: Historial -->
        <div v-if="activeTab === 'history'" class="h-full flex flex-col">
          <!-- Header del historial fijo -->
          <div class="p-4 bg-white border-b border-gray-200 flex-shrink-0">
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-lg font-semibold text-gray-900">Historial del Ticket</h3>
                <p class="text-sm text-gray-500">Cronología de eventos y cambios</p>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-xs text-gray-500">{{ visibleHistoryEntries.length }} de {{ ticketHistory.length }} eventos</span>
                <button 
                  v-if="ticketHistory.length > 3"
                  @click="showAllHistory = !showAllHistory"
                  class="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-full transition-colors"
                >
                  {{ showAllHistory ? 'Ver menos' : 'Ver todos' }}
                </button>
              </div>
            </div>
          </div>

          <!-- Estado vacío -->
          <div v-if="ticketHistory.length === 0" class="flex-1 flex items-center justify-center">
            <div class="text-center py-8">
              <div class="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <svg class="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-gray-900 mb-2">Sin historial disponible</h3>
              <p class="text-gray-600">No hay registros de cambios para este ticket.</p>
            </div>
          </div>

          <!-- Área de scroll limitada solo para eventos -->
          <div v-else class="flex-1 overflow-hidden">
            <!-- Contenedor con altura limitada y scroll solo para eventos -->
            <div class="h-full overflow-y-auto">
              <div class="p-4 space-y-3">
                <!-- Todos los eventos en el contenedor con scroll -->
                <div 
                  v-for="(entry, index) in (showAllHistory ? visibleHistoryEntries : visibleHistoryEntries.slice(0, 3))" 
                  :key="entry.id" 
                  class="bg-white rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div class="flex items-start gap-3">
                    <!-- Icono específico por tipo de evento -->
                    <div class="flex-shrink-0">
                      <div :class="getEventIconClasses(entry.type)" class="w-8 h-8 rounded-full flex items-center justify-center">
                        <component :is="getEventIconComponent(entry.type)" class="w-4 h-4" />
                      </div>
                    </div>
                    
                    <!-- Contenido del evento -->
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center justify-between mb-1">
                        <h4 class="font-semibold text-gray-900 truncate">{{ entry.action }}</h4>
                        <span class="text-xs text-gray-500 whitespace-nowrap ml-2">{{ formatRelativeDate(entry.timestamp) }}</span>
                      </div>
                      <p class="text-sm text-gray-600 leading-relaxed">{{ entry.description }}</p>
                      
                      <!-- Información adicional del evento -->
                      <div class="flex items-center justify-between mt-2">
                        <div class="flex items-center gap-2">
                          <span v-if="entry.author" class="text-xs text-gray-500">por {{ entry.author }}</span>
                          <span v-if="entry.fromValue && entry.toValue" class="text-xs bg-gray-100 px-2 py-1 rounded">
                            {{ entry.fromValue }} → {{ entry.toValue }}
                          </span>
                        </div>
                        <span class="text-xs text-gray-400">{{ formatFullDate(entry.timestamp) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tab: Adjuntos -->
        <div v-if="activeTab === 'attachments'" class="h-full overflow-y-auto">
          <div class="text-center py-8">
            <div class="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <svg class="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/>
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-gray-900 mb-2">Funcionalidad en desarrollo</h3>
            <p class="text-gray-600">La gestión de adjuntos estará disponible próximamente.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer - solo información de última actualización -->
    <template #footer>
      <div class="text-sm text-gray-500">
        Última actualización: {{ formatDate(ticket?.updatedAt) }}
      </div>
    </template>
  </BaseModal>
</template>

<script>
import BaseModal from './BaseModal.vue'
import BaseButton from './BaseButton.vue'
import TicketService from '../services/TicketService'
import { useNotificationService } from '../services/NotificationService'
import { marked } from 'marked'
import DOMPurify from 'dompurify'

// Iconos para los tabs (usando componentes SVG inline)
const InfoIcon = {
  template: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>`
}

const ChatIcon = {
  template: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>`
}

const HistoryIcon = {
  template: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>`
}

const AttachmentIcon = {
  template: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/></svg>`
}

export default {
  name: 'TicketDetailModal',
  components: {
    BaseModal,
    BaseButton,
    InfoIcon,
    ChatIcon,
    HistoryIcon,
    AttachmentIcon
  },
  directives: {
    dompurifyHtml: {
      beforeMount(el, binding) {
        el.innerHTML = binding.value || ''
      },
      updated(el, binding) {
        el.innerHTML = binding.value || ''
      }
    }
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    ticket: {
      type: Object,
      default: () => null
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      activeTab: 'details',
      newMessage: '',
      isSendingMessage: false,
      isTyping: false,
      isRefreshing: false,
      messages: [],
      ticketHistory: [],
      typingTimeout: null,
      windowWidth: window.innerWidth,
      showAllHistory: false, // Nueva propiedad para controlar la visibilidad del historial completo
      // Propiedades del editor rico
      showEmojiPicker: false,
      showPreview: false,
      showFormatHelp: false,
      currentSelection: { start: 0, end: 0 },
      activeFormats: new Set(),
      commonEmojis: ['😊', '😂', '😍', '🤔', '👍', '👎', '❤️', '😢', '😮', '😡', '🎉', '🔥', '💯', '👌', '🙌', '🤝', '💪', '🚀', '✨', '⭐', '❓', '❗', '✅', '❌', '🏆', '💡', '📝', '📊', '⏰', '📅', '🔧', '🐛']
    }
  },
  computed: {
    isVisible: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      }
    },
    isMobile() {
      return this.windowWidth <= 768
    },
    tabs() {
      const unreadMessages = this.messages.filter(m => !m.isFromUser && !m.read).length
      
      return [
        {
          id: 'details',
          label: 'Detalles',
          icon: 'InfoIcon'
        },
        {
          id: 'communication',
          label: 'Comunicación',
          shortLabel: 'Chat',
          icon: 'ChatIcon',
          badge: unreadMessages > 0 ? unreadMessages : null
        },
        {
          id: 'history',
          label: 'Historial',
          icon: 'HistoryIcon'
        },
        {
          id: 'attachments',
          label: 'Adjuntos',
          icon: 'AttachmentIcon'
        }
      ]
    },
    
    // Computed property para manejar las entradas de historial visibles
    visibleHistoryEntries() {
      return this.ticketHistory || []
    },
    
    // Computed properties para el editor rico
    messageInputRows() {
      const lines = this.newMessage.split('\n').length
      return Math.min(Math.max(lines, 3), 8)
    }
  },
  watch: {
    ticket(newTicket) {
      if (newTicket) {
        this.loadTicketData()
      }
    },
    modelValue(newVal) {
      if (newVal && this.ticket) {
        this.loadTicketData()
        // Marcar mensajes como leídos cuando se abre el modal
        this.markMessagesAsRead()
      }
    }
  },
  mounted() {
    if (this.ticket) {
      this.loadTicketData()
    }
    // Agregar listener para detectar cambios de tamaño de ventana
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    // Limpiar listeners
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    async loadTicketData() {
      // Cargar datos del ticket, mensajes e historial
      try {
        // Simular carga de mensajes
        await this.loadMessages()
        await this.loadHistory()
      } catch (error) {
        console.error('Error loading ticket data:', error)
        const notificationService = useNotificationService()
        notificationService.error('Error al cargar los detalles del ticket')
      }
    },

    async loadMessages() {
      if (!this.ticket?.id) {
        console.warn('No ticket ID available for loading messages')
        this.messages = []
        return
      }

      try {
        // Extraer solo el número del ID del ticket (sin el #)
        const ticketId = this.ticket.id.toString().replace(/^#/, '')
        
        // Cargar mensajes reales desde el webhook
        const response = await fetch('https://inmobiliaria-ecomac.app.n8n.cloud/webhook/69fe79e9-6c23-455f-aa76-64a9d245bcc8', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ticketId: ticketId
          })
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        
        // Procesar y normalizar los mensajes recibidos
        this.messages = this.processApiMessages(data)
        
        console.log(`Loaded ${this.messages.length} messages for ticket #${this.ticket.id} (ID: ${ticketId})`)
        
      } catch (error) {
        console.error('Error loading messages from API:', error)
        
        // No mostrar mensajes de fallback ni notificaciones
        this.messages = []
      }
    },
    
    // Procesar mensajes de la API y normalizarlos
    processApiMessages(apiData) {
      try {
        // La respuesta del webhook de ClickUp es un array directo de comentarios
        const comments = Array.isArray(apiData) ? apiData : []
        
        if (comments.length === 0) {
          console.log('No messages found for this ticket')
          return []
        }
        
        // Normalizar cada comentario de ClickUp
        return comments.map((comment, index) => {
          // Extraer el texto del comentario
          const content = comment.comment_text || 
                         (comment.comment && comment.comment.map(c => c.text).join('')) || 
                         'Mensaje sin contenido'
          
          // Convertir timestamp de ClickUp (milisegundos como string) a Date
          const timestamp = comment.date ? new Date(parseInt(comment.date)) : new Date()
          
          // Determinar autor del mensaje
          const user = comment.user || {}
          const author = user.username || user.email || 'Usuario Desconocido'
          
          return {
            id: comment.id || `clickup-comment-${index}`,
            content: content.trim(),
            author: author,
            timestamp: timestamp,
            isFromUser: this.determineClickUpMessageOrigin(comment),
            status: 'delivered',
            read: true,
            // Datos adicionales de ClickUp
            clickupData: {
              userId: user.id,
              userEmail: user.email,
              userInitials: user.initials,
              profilePicture: user.profilePicture,
              reactions: comment.reactions || [],
              replyCount: comment.reply_count || 0
            }
          }
        }).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)) // Ordenar por timestamp
        
      } catch (error) {
        console.error('Error processing ClickUp comments:', error)
        return []
      }
    },
    
    // Determinar si el mensaje es del usuario actual
    determineMessageOrigin(msg) {
      // Lógica para determinar el origen del mensaje
      const userIdentifiers = ['usuario', 'user', 'client', 'cliente', 'customer']
      const systemIdentifiers = ['soporte', 'support', 'system', 'sistema', 'bot', 'ai', 'elias']
      
      const author = (msg.author || msg.sender || msg.user || '').toLowerCase()
      const messageType = (msg.type || '').toLowerCase()
      
      // Si tiene identificador explícito de tipo
      if (msg.isFromUser !== undefined) {
        return msg.isFromUser
      }
      
      // Si el tipo indica que es del usuario
      if (messageType.includes('user') || messageType.includes('client')) {
        return true
      }
      
      // Si el tipo indica que es del sistema
      if (messageType.includes('system') || messageType.includes('support') || messageType.includes('bot')) {
        return false
      }
      
      // Verificar por autor
      if (userIdentifiers.some(identifier => author.includes(identifier))) {
        return true
      }
      
      if (systemIdentifiers.some(identifier => author.includes(identifier))) {
        return false
      }
      
      // Si el autor coincide con el asignado del ticket, es del soporte
      if (author === (this.ticket?.assignedTo || '').toLowerCase()) {
        return false
      }
      
      // Por defecto, asumir que es del soporte
      return false
    },
    
    // Determinar origen específico para comentarios de ClickUp
    determineClickUpMessageOrigin(comment) {
      const user = comment.user || {}
      const username = (user.username || '').toLowerCase()
      const email = (user.email || '').toLowerCase()
      
      // Lista de emails/usuarios del equipo de soporte de Ecomac
      const supportTeamEmails = [
        'elee@ecomac.cl',
        'soporte@ecomac.cl',
        'admin@ecomac.cl',
        'tech@ecomac.cl'
      ]
      
      // Lista de nombres de usuarios del equipo de soporte
      const supportTeamNames = [
        'edward c. lee',
        'elias ai',
        'soporte técnico',
        'administrador'
      ]
      
      // Si el email coincide con el equipo de soporte, es del soporte
      if (supportTeamEmails.some(supportEmail => email.includes(supportEmail))) {
        return false // Es del soporte
      }
      
      // Si el username coincide con el equipo de soporte, es del soporte
      if (supportTeamNames.some(supportName => username.includes(supportName))) {
        return false // Es del soporte
      }
      
      // Si el autor coincide con el asignado del ticket, es del soporte
      if (username === (this.ticket?.assignedTo || '').toLowerCase()) {
        return false // Es del soporte
      }
      
      // Por defecto, si no es del equipo de soporte, es del cliente
      return true // Es del usuario/cliente
    },
    
    // Mensajes de fallback en caso de error
    getFallbackMessages() {
      const fallbackMessages = [
        {
          id: 'fallback-1',
          content: `Hola! He recibido tu reporte sobre "${this.ticket?.title}". Estoy revisando los detalles y te mantendré informado sobre el progreso.`,
          author: this.ticket?.assignedTo || 'Soporte Técnico',
          timestamp: new Date(Date.now() - 60 * 60 * 1000), // Hace 1 hora
          isFromUser: false,
          status: 'delivered',
          read: false
        }
      ]
      
      if (this.ticket?.status === 'in_progress' || this.ticket?.status === 'en_progreso') {
        fallbackMessages.push({
          id: 'fallback-2',
          content: 'Ya comencé a trabajar en tu ticket. He identificado el problema y estoy implementando la solución. Te actualizaré cuando esté listo para testing.',
          author: this.ticket?.assignedTo || 'Soporte Técnico',
          timestamp: new Date(Date.now() - 30 * 60 * 1000), // Hace 30 minutos
          isFromUser: false,
          status: 'delivered',
          read: false
        })
      }
      
      return fallbackMessages
    },

    async loadHistory() {
      // ACTUALIZADO: Usar historial real del ticket normalizado
      if (this.ticket && this.ticket.history && Array.isArray(this.ticket.history)) {
        // Usar el historial real del ticket ya construido por TicketService
        this.ticketHistory = [...this.ticket.history]
      } else {
        // Fallback: historial simulado si no hay datos reales
        this.ticketHistory = [
          {
            id: 1,
            type: 'created',
            action: 'Ticket creado',
            description: `Nuevo ticket reportado: "${this.ticket?.title}"`,
            author: 'Sistema',
            timestamp: this.ticket?.createdAt
          },
          {
            id: 2,
            type: 'assignment',
            action: 'Ticket asignado',
            description: `Asignado a ${this.ticket?.assignedTo}`,
            author: 'Sistema',
            timestamp: this.ticket?.createdAt
          }
        ]

        if (this.ticket?.status === 'in_progress' || this.ticket?.status === 'en_progreso') {
          this.ticketHistory.push({
            id: 3,
            type: 'status_change',
            action: 'Estado actualizado',
            description: 'Ticket movido a "En Progreso"',
            author: this.ticket?.assignedTo,
            timestamp: this.ticket?.updatedAt
          })
        }
      }
    },

    async handleSendMessage() {
      if (!this.newMessage.trim() || this.isSendingMessage) return

      const messageContent = this.newMessage.trim()
      const messageId = Date.now()

      // Agregar mensaje del usuario inmediatamente
      const userMessage = {
        id: messageId,
        content: messageContent,
        author: 'Usuario',
        timestamp: new Date(),
        isFromUser: true,
        status: 'sending'
      }

      this.messages.push(userMessage)
      this.newMessage = ''
      this.isSendingMessage = true

      // Scroll al final
      this.$nextTick(() => {
        this.scrollToBottom()
      })

      try {
        // Extraer solo el número del ID del ticket (sin el #)
        const ticketId = this.ticket.id.toString().replace(/^#/, '')
        
        console.log(`💬 Enviando mensaje para ticket #${this.ticket.id} (ID: ${ticketId})`)
        
        // Enviar mensaje al webhook de n8n para ClickUp
        const response = await fetch('https://inmobiliaria-ecomac.app.n8n.cloud/webhook/55b0c92a-d6ca-41a4-819c-10a8dbced2bd', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ticketId: ticketId,
            message: messageContent,
            timestamp: new Date().toISOString(),
            source: 'portal-soporte'
          })
        })

        // Actualizar estado del mensaje a 'sent'
        const message = this.messages.find(m => m.id === messageId)
        if (message) {
          message.status = 'sent'
        }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const responseData = await response.json()
        console.log('📨 Mensaje enviado exitosamente a ClickUp:', responseData)
        
        // Actualizar estado a 'delivered'
        if (message) {
          message.status = 'delivered'
        }
        
        // Notificar éxito
        const notificationService = useNotificationService()
        notificationService.success('Mensaje enviado correctamente')
        
        // Recargar mensajes después de un pequeño delay para ver la respuesta
        setTimeout(() => {
          this.refreshChat()
        }, 2000)

      } catch (error) {
        console.error('❌ Error sending message to ClickUp webhook:', error)
        
        const message = this.messages.find(m => m.id === messageId)
        if (message) {
          message.status = 'error'
        }
        
        const notificationService = useNotificationService()
        notificationService.error('Error al enviar el mensaje. Por favor, inténtalo de nuevo.')
      } finally {
        this.isSendingMessage = false
      }
    },

    markMessagesAsRead() {
      this.messages.forEach(message => {
        if (!message.isFromUser) {
          message.read = true
        }
      })
    },

    scrollToBottom() {
      if (this.$refs.messagesContainer) {
        this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight
      }
    },

    handleClose() {
      this.isVisible = false
    },

    handleAvatarError(event) {
      console.warn('Error loading avatar:', event.target.src);
      // Ocultar imagen y mostrar iniciales como fallback
      event.target.style.display = 'none';
      // El v-else-if se encargará de mostrar las iniciales
    },

    // Métodos para la comunicación mejorada
    async refreshChat() {
      if (this.isRefreshing) return;
      
      this.isRefreshing = true;
      try {
        // Cargar mensajes reales desde la API
        await this.loadMessages();
        
        // Scroll al final después de cargar mensajes
        this.$nextTick(() => {
          this.scrollToBottom();
        });
        
        const notificationService = useNotificationService();
        notificationService.success('Chat actualizado correctamente');
      } catch (error) {
        console.error('Error refreshing chat:', error);
        const notificationService = useNotificationService();
        notificationService.error('Error al actualizar el chat');
      } finally {
        this.isRefreshing = false;
      }
    },

    sendQuickMessage(message) {
      this.newMessage = message;
      this.handleSendMessage();
    },

    insertQuickReply(reply) {
      if (this.newMessage.trim()) {
        this.newMessage += '\n\n' + reply;
      } else {
        this.newMessage = reply;
      }
      // Focus en el textarea
      this.$nextTick(() => {
        const textarea = this.$el.querySelector('textarea');
        if (textarea) {
          textarea.focus();
          textarea.setSelectionRange(textarea.value.length, textarea.value.length);
        }
      });
    },

    handleTyping() {
      // Simular indicador de "está escribiendo"
      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout);
      }
      
      // Aquí se enviaría una señal al servidor de que el usuario está escribiendo
      this.typingTimeout = setTimeout(() => {
        // Señal de que dejó de escribir
      }, 1000);
    },

    openFileDialog() {
      // Funcionalidad para adjuntar archivos (placeholder)
      const notificationService = useNotificationService();
      notificationService.info('Funcionalidad de adjuntos próximamente disponible');
    },

    handleResize() {
      this.windowWidth = window.innerWidth
    },

    // Métodos de utilidad para formatear datos
    formatDate(dateString) {
      if (!dateString) return 'No disponible'
      return TicketService.formatDate(dateString)
    },

    formatMessageTime(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const now = new Date()
      const diffInMinutes = Math.floor((now - date) / (1000 * 60))

      if (diffInMinutes < 1) return 'Ahora'
      if (diffInMinutes < 60) return `Hace ${diffInMinutes}m`
      if (diffInMinutes < 1440) return `Hace ${Math.floor(diffInMinutes / 60)}h`
      return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })
    },

    formatStatusText(status) {
      return TicketService.formatStatusText(status)
    },

    formatPriorityText(priority) {
      return TicketService.formatPriorityText(priority)
    },

    getStatusBadgeClass(status) {
      return TicketService.getStatusBadgeClass(status)
    },

    getPriorityBadgeClass(priority) {
      return TicketService.getPriorityBadgeClass(priority)
    },

    getAssigneeInitials(assignedTo) {
      if (!assignedTo || assignedTo === 'Sin asignar') return 'SA'
      if (assignedTo === 'Elias AI') return 'EA'
      
      const name = assignedTo.trim()
      const words = name.split(' ').filter(word => word.length > 0)
      
      if (words.length >= 2) {
        return (words[0][0] + words[words.length - 1][0]).toUpperCase()
      } else if (words.length === 1) {
        const word = words[0]
        if (word.length >= 2) {
          return word.substring(0, 2).toUpperCase()
        } else {
          return word.toUpperCase() + 'A'
        }
      }
      
      return 'AS'
    },

    getProgressPercentage(ticket) {
      if (!ticket) return 0
      
      // Si viene del webhook procesado
      if (typeof ticket.progressPercent === 'number') {
        return Math.round(ticket.progressPercent)
      }
      
      // Fallback basado en estado
      const progressMap = {
        'open': 10,
        'abierto': 10,
        'pending': 5,
        'in_progress': 50,
        'en_progreso': 50,
        'resolved': 100,
        'resuelto': 100,
        'closed': 100,
        'cerrado': 100
      }
      
      return progressMap[ticket.status?.toLowerCase()] || 0
    },

    getProgressBarClass(status) {
      const progressClasses = {
        'open': 'bg-gradient-to-r from-red-500 to-red-600',
        'abierto': 'bg-gradient-to-r from-red-500 to-red-600',
        'pending': 'bg-gradient-to-r from-gray-500 to-gray-600',
        'in_progress': 'bg-gradient-to-r from-yellow-500 to-yellow-600',
        'en_progreso': 'bg-gradient-to-r from-yellow-500 to-yellow-600',
        'resolved': 'bg-gradient-to-r from-green-500 to-green-600',
        'resuelto': 'bg-gradient-to-r from-green-500 to-green-600',
        'closed': 'bg-gradient-to-r from-gray-500 to-gray-600',
        'cerrado': 'bg-gradient-to-r from-gray-500 to-gray-600'
      }
      return progressClasses[status?.toLowerCase()] || 'bg-gradient-to-r from-gray-400 to-gray-500'
    },

    isTicketResolved(status) {
      const resolvedStates = ['resolved', 'resuelto', 'closed', 'cerrado', 'complete', 'done']
      return resolvedStates.includes(status?.toLowerCase())
    },

    getStatusIndicatorClass(status) {
      const statusClasses = {
        'open': 'bg-red-500',
        'abierto': 'bg-red-500',
        'pending': 'bg-red-500',
        'in_progress': 'bg-yellow-500',
        'en_progreso': 'bg-yellow-500',
        'resolved': 'bg-green-500',
        'resuelto': 'bg-green-500',
        'closed': 'bg-gray-500',
        'cerrado': 'bg-gray-500'
      }
      return statusClasses[status?.toLowerCase()] || 'bg-gray-400'
    },

    getProgressColor(status) {
      const progressColors = {
        'open': '#ef4444',
        'abierto': '#ef4444',
        'pending': '#6b7280',
        'in_progress': '#eab308',
        'en_progreso': '#eab308',
        'resolved': '#22c55e',
        'resuelto': '#22c55e',
        'closed': '#6b7280',
        'cerrado': '#6b7280'
      }
      return progressColors[status?.toLowerCase()] || '#9ca3af'
    },

    // Métodos para manejar datos específicos del ticket
    getPriorityIndicatorClass(priority) {
      const priorityClasses = {
        'urgent': 'bg-red-500',
        'alta': 'bg-red-500',
        'high': 'bg-red-500',
        'normal': 'bg-yellow-500',
        'media': 'bg-yellow-500',
        'medium': 'bg-yellow-500',
        'low': 'bg-green-500',
        'baja': 'bg-green-500'
      }
      return priorityClasses[priority?.toLowerCase()] || 'bg-gray-400'
    },

    getPriorityTextClass(priority) {
      const priorityTextClasses = {
        'urgent': 'text-red-700',
        'alta': 'text-red-700',
        'high': 'text-red-700',
        'normal': 'text-yellow-700',
        'media': 'text-yellow-700',
        'medium': 'text-yellow-700',
        'low': 'text-green-700',
        'baja': 'text-green-700'
      }
      return priorityTextClasses[priority?.toLowerCase()] || 'text-gray-700'
    },

    getTimeEstimate(ticket) {
      if (!ticket) return null
      
      // PRIORIDAD 1: Usar tiempo estimado ya extraído por TicketService
      if (ticket.timeEstimate) {
        return ticket.timeEstimate
      }
      
      // FALLBACK: Si por alguna razón no tiene tiempo estimado, no mostrar nada
      return null
    },

    formatDurationFromMs(milliseconds) {
      if (!milliseconds || milliseconds <= 0) return null
      
      const hours = Math.floor(milliseconds / (1000 * 60 * 60))
      const days = Math.floor(hours / 24)
      
      if (days > 0) {
        const remainingHours = hours % 24
        if (remainingHours === 0) {
          return `${days} ${days === 1 ? 'día' : 'días'}`
        } else {
          return `${days}d ${remainingHours}h`
        }
      } else if (hours > 0) {
        const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60))
        if (minutes === 0) {
          return `${hours} ${hours === 1 ? 'hora' : 'horas'}`
        } else {
          return `${hours}h ${minutes}m`
        }
      } else {
        const minutes = Math.floor(milliseconds / (1000 * 60))
        return `${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`
      }
    },

    // Métodos para manejo de markdown
    isMarkdownContent(content) {
      if (!content) return false
      
      // Detectar patrones comunes de markdown
      const markdownPatterns = [
        /#{1,6}\s+/, // Headers
        /\*\*.*\*\*/, // Bold
        /\*.*\*/, // Italic
        /```[\s\S]*?```/, // Code blocks
        /`[^`]+`/, // Inline code
        /^\s*[-*+]\s+/m, // Unordered lists
        /^\s*\d+\.\s+/m, // Ordered lists
        /\[.*?\]\(.*?\)/, // Links
        /^\s*>\s+/m, // Blockquotes
        /\|.*\|/, // Tables
        /---+/, // Horizontal rules
      ]
      
      return markdownPatterns.some(pattern => pattern.test(content))
    },

    getMarkdownContent(content) {
      if (!content) return ''
      
      try {
        // Configurar marked con opciones seguras
        marked.setOptions({
          breaks: true, // Convertir saltos de línea en <br>
          gfm: true, // GitHub Flavored Markdown
          headerIds: false, // No generar IDs en headers
          mangle: false, // No alterar texto
          sanitize: false, // DOMPurify se encarga de la sanitización
        })
        
        // Convertir markdown a HTML
        const rawHtml = marked.parse(content)
        
        // Sanitizar HTML con DOMPurify
        return DOMPurify.sanitize(rawHtml, {
          ALLOWED_TAGS: [
            'p', 'br', 'strong', 'em', 'u', 'code', 'pre', 
            'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
            'ul', 'ol', 'li', 'blockquote', 'a', 
            'table', 'thead', 'tbody', 'tr', 'th', 'td',
            'hr', 'del', 'ins'
          ],
          ALLOWED_ATTR: ['href', 'title', 'target', 'rel'],
          ALLOW_DATA_ATTR: false,
        })
        
      } catch (error) {
        console.error('Error parsing markdown:', error)
        // Fallback a texto plano si hay error
        return `<p>${content.replace(/\n/g, '<br>')}</p>`
      }
    },

    getTicketCategory(ticket) {
      if (!ticket) return 'General'
      
      // PRIORIDAD 1: Usar categoría ya extraída por TicketService
      if (ticket.category) {
        return ticket.category
      }
      
      // FALLBACK: Si por alguna razón no tiene categoría, usar 'General'
      return 'General'
    },

    // Métodos específicos para el historial de eventos
    getEventIconComponent(eventType) {
      // Crear componentes SVG dinámicos para cada tipo de evento
      const iconComponents = {
        'created': {
          template: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
          </svg>`
        },
        'assignment': {
          template: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
          </svg>`
        },
        'status_change': {
          template: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
          </svg>`
        },
        'priority_change': {
          template: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"/>
          </svg>`
        },
        'comment': {
          template: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
          </svg>`
        },
        'attachment': {
          template: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/>
          </svg>`
        },
        'subtask': {
          template: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
          </svg>`
        },
        'escalation': {
          template: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 11l5-5m0 0l5 5m-5-5v12"/>
          </svg>`
        },
        'time_tracking': {
          template: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>`
        },
        'custom_field': {
          template: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
          </svg>`
        },
        'automation': {
          template: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
          </svg>`
        }
      }
      
      return iconComponents[eventType] || iconComponents['created']
    },

    getEventIconClasses(eventType) {
      const classMap = {
        'created': 'bg-green-100 text-green-600',
        'assignment': 'bg-blue-100 text-blue-600',
        'status_change': 'bg-orange-100 text-orange-600',
        'priority_change': 'bg-red-100 text-red-600',
        'comment': 'bg-purple-100 text-purple-600',
        'attachment': 'bg-gray-100 text-gray-600',
        'subtask': 'bg-indigo-100 text-indigo-600',
        'escalation': 'bg-red-100 text-red-600',
        'time_tracking': 'bg-yellow-100 text-yellow-600',
        'custom_field': 'bg-teal-100 text-teal-600',
        'automation': 'bg-slate-100 text-slate-600'
      }
      
      return classMap[eventType] || 'bg-gray-100 text-gray-600'
    },

    formatRelativeDate(dateString) {
      if (!dateString) return ''
      
      const date = new Date(dateString)
      const now = new Date()
      const diffInSeconds = Math.floor((now - date) / 1000)
      const diffInMinutes = Math.floor(diffInSeconds / 60)
      const diffInHours = Math.floor(diffInMinutes / 60)
      const diffInDays = Math.floor(diffInHours / 24)
      
      if (diffInSeconds < 60) {
        return 'Hace unos segundos'
      } else if (diffInMinutes < 60) {
        return `Hace ${diffInMinutes} min${diffInMinutes === 1 ? '' : 's'}`
      } else if (diffInHours < 24) {
        return `Hace ${diffInHours} hora${diffInHours === 1 ? '' : 's'}`
      } else if (diffInDays < 30) {
        return `Hace ${diffInDays} día${diffInDays === 1 ? '' : 's'}`
      } else {
        return date.toLocaleDateString('es-ES', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        })
      }
    },

    formatFullDate(dateString) {
      if (!dateString) return ''
      
      const date = new Date(dateString)
      return date.toLocaleDateString('es-ES', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    
    // ==================== MÉTODOS DEL EDITOR RICO ====================
    
    // Manejo de eventos del teclado
    handleKeydown(event) {
      // Manejar atajos de teclado
      if (event.metaKey || event.ctrlKey) {
        switch (event.key) {
          case 'b':
            event.preventDefault()
            this.toggleBold()
            break
          case 'i':
            event.preventDefault()
            this.toggleItalic()
            break
          case '`':
            event.preventDefault()
            this.toggleCode()
            break
          case 'Enter':
            if (!event.shiftKey) {
              event.preventDefault()
              this.handleSendMessage()
            }
            break
        }
      } else if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        this.handleSendMessage()
      }
      
      // Actualizar selección después de cualquier tecla
      this.$nextTick(() => {
        this.handleSelection()
      })
    },
    
    // Manejo de selección de texto
    handleSelection() {
      if (this.$refs.messageInput) {
        const textarea = this.$refs.messageInput
        this.currentSelection = {
          start: textarea.selectionStart,
          end: textarea.selectionEnd
        }
        this.updateActiveFormats()
      }
    },
    
    // Actualizar formatos activos basados en la selección
    updateActiveFormats() {
      const { start, end } = this.currentSelection
      const selectedText = this.newMessage.substring(start, end)
      
      this.activeFormats.clear()
      
      // Detectar si el texto seleccionado tiene formato
      if (selectedText.startsWith('**') && selectedText.endsWith('**')) {
        this.activeFormats.add('bold')
      }
      if (selectedText.startsWith('*') && selectedText.endsWith('*') && !selectedText.startsWith('**')) {
        this.activeFormats.add('italic')
      }
      if (selectedText.startsWith('`') && selectedText.endsWith('`')) {
        this.activeFormats.add('code')
      }
    },
    
    // Verificar si un formato está activo
    isFormatActive(format) {
      return this.activeFormats.has(format)
    },
    
    // Aplicar formato al texto seleccionado
    applyFormat(prefix, suffix = null) {
      if (!this.$refs.messageInput) return
      
      const textarea = this.$refs.messageInput
      const { start, end } = this.currentSelection
      const selectedText = this.newMessage.substring(start, end)
      const beforeText = this.newMessage.substring(0, start)
      const afterText = this.newMessage.substring(end)
      
      suffix = suffix || prefix
      
      let newText
      let newCursorPos
      
      if (selectedText) {
        // Hay texto seleccionado
        newText = `${beforeText}${prefix}${selectedText}${suffix}${afterText}`
        newCursorPos = start + prefix.length + selectedText.length + suffix.length
      } else {
        // No hay selección, insertar formato vacío
        newText = `${beforeText}${prefix}${suffix}${afterText}`
        newCursorPos = start + prefix.length
      }
      
      this.newMessage = newText
      
      // Restaurar cursor/selección
      this.$nextTick(() => {
        textarea.focus()
        if (selectedText) {
          textarea.setSelectionRange(newCursorPos, newCursorPos)
        } else {
          textarea.setSelectionRange(newCursorPos, newCursorPos)
        }
      })
    },
    
    // Métodos de formato específicos
    toggleBold() {
      this.applyFormat('**')
    },
    
    toggleItalic() {
      this.applyFormat('*')
    },
    
    toggleCode() {
      this.applyFormat('`')
    },
    
    insertList() {
      if (!this.$refs.messageInput) return
      
      const textarea = this.$refs.messageInput
      const { start } = this.currentSelection
      const beforeText = this.newMessage.substring(0, start)
      const afterText = this.newMessage.substring(start)
      
      // Insertar lista al principio de línea
      const newText = `${beforeText}\n- `
      this.newMessage = newText + afterText
      
      this.$nextTick(() => {
        textarea.focus()
        const newPos = newText.length
        textarea.setSelectionRange(newPos, newPos)
      })
    },
    
    insertLink() {
      const url = prompt('Ingresa la URL:')
      if (!url) return
      
      const linkText = prompt('Texto del enlace:', url) || url
      this.applyFormat(`[${linkText}](`, ')')
    },
    
    // Manejo de emojis
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker
      if (!this.showEmojiPicker) {
        // Cerrar otros paneles
        this.showFormatHelp = false
      }
    },
    
    insertEmoji(emoji) {
      if (!this.$refs.messageInput) return
      
      const textarea = this.$refs.messageInput
      const { start } = this.currentSelection
      const beforeText = this.newMessage.substring(0, start)
      const afterText = this.newMessage.substring(start)
      
      this.newMessage = beforeText + emoji + afterText
      
      this.$nextTick(() => {
        textarea.focus()
        const newPos = start + emoji.length
        textarea.setSelectionRange(newPos, newPos)
      })
      
      this.showEmojiPicker = false
    },
    
    // Vista previa de markdown
    getMarkdownPreview(content) {
      if (!content) return ''
      
      try {
        // Configurar marked para vista previa
        marked.setOptions({
          breaks: true,
          gfm: true,
          headerIds: false,
          mangle: false,
          sanitize: false,
        })
        
        const rawHtml = marked.parse(content)
        
        return DOMPurify.sanitize(rawHtml, {
          ALLOWED_TAGS: [
            'p', 'br', 'strong', 'em', 'code', 'pre', 
            'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
            'ul', 'ol', 'li', 'blockquote', 'a',
            'hr', 'del', 'ins'
          ],
          ALLOWED_ATTR: ['href', 'title', 'target', 'rel'],
          ALLOW_DATA_ATTR: false,
        })
      } catch (error) {
        console.error('Error parsing markdown preview:', error)
        return `<p>${content.replace(/\n/g, '<br>')}</p>`
      }
    },
    
    // Cerrar paneles cuando se hace clic fuera
    handleDocumentClick(event) {
      if (!event.target.closest('.relative')) {
        this.showEmojiPicker = false
      }
    }
  }
}
</script>

<style scoped>
/* Animaciones personalizadas */
@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.animate-shimmer {
  animation: shimmer 2s infinite;
}

/* Scroll suave para el área de mensajes */
.overflow-y-auto {
  scroll-behavior: smooth;
}

/* Estilos mejorados para los mensajes */
.max-w-xs {
  max-width: 20rem;
}

.max-w-md {
  max-width: 28rem;
}

.max-w-lg {
  max-width: 32rem;
}

/* Mobile-first responsive adjustments */
@media (max-width: 768px) {
  /* Modal específico para móviles */
  .mobile-modal .ticket-detail-modal {
    margin: 0 !important;
    height: 100vh !important;
    max-height: 100vh !important;
    border-radius: 0 !important;
  }
  
  /* Header compacto en móviles */
  .mobile-modal .relative.bg-gradient-to-br {
    border-radius: 0 !important;
  }
  
  /* Grid layouts móviles */
  .grid-cols-1.lg\\:grid-cols-4 {
    grid-template-columns: 1fr;
  }
  
  .max-w-xs.lg\\:max-w-lg.xl\\:max-w-2xl {
    max-width: 16rem;
  }
  
  /* Espaciado del header móvil */
  .flex.items-start.gap-8 {
    gap: 1rem;
  }
  
  /* Tipografía móvil */
  .text-2xl.lg\\:text-3xl {
    font-size: 1.25rem;
    line-height: 1.75rem;
  }
  
  /* Grid de metadata compacto */
  .grid.grid-cols-1.sm\\:grid-cols-2.lg\\:grid-cols-5 {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  
  /* Pills de navegación móviles */
  .mobile-modal .bg-gray-50.rounded-2xl {
    background: rgba(249, 250, 251, 0.95);
    backdrop-filter: blur(8px);
  }
  
  /* Área de mensajes móvil */
  .mobile-modal .overflow-y-auto {
    -webkit-overflow-scrolling: touch;
  }
  
  /* Input area móvil */
  .mobile-modal textarea {
    font-size: 16px; /* Evita zoom en iOS */
    min-height: 60px;
  }
  
  /* Botones táctiles */
  .mobile-modal button {
    min-height: 44px;
    touch-action: manipulation;
  }
  
  /* Footer sticky en móviles */
  .mobile-modal .bg-white.border-t {
    position: sticky;
    bottom: 0;
    z-index: 10;
  }
}

@media (min-width: 769px) and (max-width: 1023px) {
  /* Tablet adjustments */
  .max-w-5xl {
    max-width: 100%;
  }
  
  .grid.grid-cols-1.lg\\:grid-cols-4 {
    grid-template-columns: 1fr 300px;
  }
  
  .lg\\:col-span-3 {
    grid-column: span 1;
  }
}

@media (min-width: 1024px) {
  /* Desktop optimizations para 75vw */
  .max-w-5xl {
    max-width: 64rem;
  }
}

/* Mejorar la apariencia del textarea */
textarea:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Gradiente para las barras de progreso */
.bg-gradient-radial {
  background: radial-gradient(ellipse at center, var(--tw-gradient-stops));
}

/* Estilos personalizados para contenido markdown */
.markdown-content {
  line-height: 1.6;
}

.markdown-content h1,
.markdown-content h2,
.markdown-content h3,
.markdown-content h4,
.markdown-content h5,
.markdown-content h6 {
  margin-top: 1.5em;
  margin-bottom: 0.5em;
  font-weight: 600;
  color: #1f2937;
}

.markdown-content h1 {
  font-size: 1.5em;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.3em;
}

.markdown-content h2 {
  font-size: 1.3em;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.2em;
}

.markdown-content h3 {
  font-size: 1.1em;
}

.markdown-content p {
  margin-bottom: 1em;
}

.markdown-content strong {
  font-weight: 600;
  color: #111827;
}

.markdown-content em {
  font-style: italic;
  color: #374151;
}

.markdown-content code {
  background-color: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.875em;
  padding: 0.125rem 0.25rem;
  color: #dc2626;
}

.markdown-content pre {
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  overflow-x: auto;
  padding: 1rem;
  margin: 1em 0;
}

.markdown-content pre code {
  background: none;
  border: none;
  padding: 0;
  color: #374151;
  font-size: 0.875rem;
}

.markdown-content ul,
.markdown-content ol {
  margin: 1em 0;
  padding-left: 1.5em;
}

.markdown-content li {
  margin-bottom: 0.5em;
}

.markdown-content blockquote {
  border-left: 4px solid #3b82f6;
  background-color: #eff6ff;
  margin: 1em 0;
  padding: 0.5em 1em;
  font-style: italic;
}

.markdown-content a {
  color: #3b82f6;
  text-decoration: underline;
  transition: color 0.2s;
}

.markdown-content a:hover {
  color: #1d4ed8;
}

.markdown-content table {
  width: 100%;
  border-collapse: collapse;
  margin: 1em 0;
  font-size: 0.875rem;
}

.markdown-content th,
.markdown-content td {
  border: 1px solid #e5e7eb;
  padding: 0.5rem;
  text-align: left;
}

.markdown-content th {
  background-color: #f9fafb;
  font-weight: 600;
}

.markdown-content hr {
  border: none;
  border-top: 2px solid #e5e7eb;
  margin: 2em 0;
}

.markdown-content del {
  text-decoration: line-through;
  color: #6b7280;
}

.markdown-content ins {
  text-decoration: underline;
  background-color: #dcfce7;
  color: #166534;
  padding: 0.125rem;
  border-radius: 0.25rem;
}
</style>
